import { Button, Form, message, Input, Popconfirm } from 'antd';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { cloneDeep, debounce, isEmpty, isEqual } from 'lodash';
import { useQueryCriticalEvent } from '@src/services/mutations';

import {
  trimMappedEventRules,
  validateMappedEventData,
  isEmptyMappedEventRules,
  validateMappedEventName,
} from '@src/shared/utils/eventBuilder';
import { useLatest } from '@src/shared/hooks';
import MappingRulesBuilder from './MappingRulesBuilder';
import { ActionsWrapper, Header, Wrapper, StyledFormError, SubTitle } from './styled';
import { formatPercentage } from '@src/shared/utils';

export const DEFAULT_REQUIRED_ERROR_MESSAGE = '* Required fields in red are missing.';

// Type for creating a new critical event - only requires 3 fields
export type CreateCriticalEvent = {
  name: string;
  rules: CriticalEventRule[][];
  is_milestone: boolean;
};

// Type for the result of onChange/onSave - can be partial when editing, minimal when creating
export type CriticalEventFormResult = CriticalEvent | CreateCriticalEvent;

interface IProps {
  initialEvent?: CriticalEvent | Partial<CriticalEvent>;
  onChange?: (event: CriticalEventFormResult) => void;
  onSave?: (event?: CriticalEventFormResult) => void;
  onCancel?: (event?: Partial<CriticalEvent>) => void;
  cancelText?: string;
  isLoading?: boolean;
}

const CriticalEventForm: React.FC<IProps> = ({
  initialEvent,
  onChange,
  onSave,
  onCancel,
  cancelText,
  isLoading,
}) => {
  const [validation, setValidation] = useState<{
    failed: boolean;
    fields: string[];
    message?: string;
    errorIndex?: Record<number, number[]>;
  }>({ failed: false, fields: [] });
  const latestValidationRef = useLatest(validation);

  const [criticalEvent, setCriticalEvent] = useState<Partial<CriticalEvent>>();
  const [footerError, setFooterError] = useState<boolean>(false);
  const [footerForm] = Form.useForm();
  const [popconfirmVisible, setPopconfirmVisible] = useState(false);
  const [queryResult, setQueryResult] = useState<{
    data: Record<string, string>;
    time_range: [string, string];
  } | null>(null);
  const [pendingSaveEvent, setPendingSaveEvent] = useState<CriticalEventFormResult | undefined>();
  const { mutateAsync: queryCriticalEvent, isLoading: isQuerying } = useQueryCriticalEvent();

  // Determine if this is a create or edit operation
  const isCreating = useMemo(() => {
    return !initialEvent?.id;
  }, [initialEvent]);

  const trimMappedEvent = useMemo(() => {
    if (!initialEvent) return undefined;
    return {
      ...initialEvent,
      rules: trimMappedEventRules(initialEvent?.rules as any),
    };
  }, [initialEvent]);

  const isEmptyRules = useMemo(
    () => isEmptyMappedEventRules(criticalEvent?.rules as any),
    [criticalEvent?.rules],
  );

  useEffect(() => {
    const cloned = cloneDeep(initialEvent);
    setCriticalEvent(cloned);
    footerForm.setFieldsValue({ ...cloned });
  }, [initialEvent, footerForm]);

  useEffect(() => {
    if (criticalEvent) {
      // When creating, only send the 3 required fields
      if (isCreating) {
        const createEvent: CreateCriticalEvent = {
          name: criticalEvent.name || '',
          rules: criticalEvent.rules || [],
          is_milestone: criticalEvent.is_milestone || false,
        };
        onChange?.(createEvent);
      } else {
        onChange?.(criticalEvent as CriticalEvent);
      }
    }
  }, [criticalEvent, onChange, isCreating]);

  useEffect(() => {
    if (trimMappedEvent) {
      const res = validateMappedEventData(trimMappedEvent as any, true);
      if (isEqual(latestValidationRef.current.fields, res.fields)) {
        setValidation({ failed: false, fields: [] });
      }
    }
  }, [trimMappedEvent, latestValidationRef]);

  const handleRulesChange = useCallback(
    (value) => {
      if (!criticalEvent) return;
      setCriticalEvent({
        ...criticalEvent,
        rules: value,
      });
    },
    [criticalEvent],
  );

  const onValidate = useCallback(() => {
    const eventNew = {
      ...criticalEvent,
      rules: trimMappedEventRules(criticalEvent?.rules as any),
    };

    const res = validateMappedEventData(eventNew as any, true);
    if (!res.status) {
      if (!res.fields.length) {
        message.warning(`${res.key ? res.key.join(' OR ') : ''} ${res.result}`);
      } else {
        const msg =
          res.result === 'is null' ? DEFAULT_REQUIRED_ERROR_MESSAGE : `${res.key} ${res.result}`;
        setValidation({
          failed: true,
          fields: res.fields,
          message: msg,
          errorIndex: res.errorIndex,
        });
      }
    }
    return res.status;
  }, [criticalEvent]);

  const onCheckCoverageBeforeSave = useCallback(
    async (eventName?: string) => {
      if (!criticalEvent) return;
      const name = eventName || criticalEvent.name || '';
      const trimmedRules = trimMappedEventRules(criticalEvent.rules);

      // Prepare the event to save
      let eventToSave: CriticalEventFormResult;
      if (isCreating) {
        eventToSave = {
          name: name.trim(),
          rules: trimmedRules,
          is_milestone: criticalEvent.is_milestone || false,
        };
      } else {
        eventToSave = {
          ...criticalEvent,
          name: name.trim(),
          rules: trimmedRules,
        } as CriticalEvent;
      }

      // Query the API to check coverage
      try {
        const result = await queryCriticalEvent({
          name: name.trim(),
          rules: trimmedRules,
        });
        setQueryResult(result);
        setPendingSaveEvent(eventToSave);
        setPopconfirmVisible(true);
      } catch (error) {
        // If query fails, still allow saving
        console.error('Failed to query critical event coverage:', error);
        onSave?.(eventToSave);
      }
    },
    [criticalEvent, isCreating, onSave, queryCriticalEvent],
  );

  const validateBeforeCreate = useCallback(() => {
    footerForm
      .validateFields()
      .then((values) => {
        if (!isEmpty(values)) {
          if (!values.name && !values.event_name) {
            setFooterError(true);
            return;
          }
        }
        void onCheckCoverageBeforeSave();
      })
      .catch((e) => {
        console.error(e);
      });
  }, [footerForm, onCheckCoverageBeforeSave]);

  const handleSaveClick = useCallback(
    (e) => {
      e.stopPropagation();
      const status = onValidate();
      if (!status) {
        return;
      }
      validateBeforeCreate();
    },
    [onValidate, validateBeforeCreate],
  );

  const handleConfirmAndSave = useCallback(() => {
    if (pendingSaveEvent) {
      onSave?.(pendingSaveEvent);
      setPopconfirmVisible(false);
      setQueryResult(null);
      setPendingSaveEvent(undefined);
    }
  }, [pendingSaveEvent, onSave]);

  const handleBackToEdit = useCallback(() => {
    setPopconfirmVisible(false);
    setQueryResult(null);
    setPendingSaveEvent(undefined);
  }, []);

  const handleCancelClick = useCallback(() => {
    onCancel?.(criticalEvent || undefined);
  }, [onCancel, criticalEvent]);

  const debouncedOnValuesChange = useMemo(
    () =>
      debounce((values) => {
        if ('event_name' in values || 'name' in values) {
          setFooterError(false);
        }
        setCriticalEvent((preState) => {
          if (!preState) return preState;
          return {
            ...preState,
            ...values,
          };
        });
      }, 300),
    [],
  );

  return (
    <Wrapper>
      <Form form={footerForm} onValuesChange={debouncedOnValuesChange}>
        <Header>
          <Form.Item
            name={'name'}
            style={{ flex: 1, marginBottom: 0, marginLeft: 8 }}
            label={
              <div>
                Event Name <span style={{ color: '#ff4d4f' }}></span>
              </div>
            }
            required={false}
            validateStatus={validation?.fields?.includes('name') ? 'error' : undefined}
            rules={[
              () => ({
                validator(_, value) {
                  const { failed, message: errorMessage } = validateMappedEventName(value);
                  if (failed) {
                    setValidation({ failed: true, fields: ['name'], message: errorMessage });
                    return Promise.reject(new Error(errorMessage));
                  }
                  setValidation({ failed: false, fields: [] });
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input />
          </Form.Item>
        </Header>
      </Form>
      {(validation?.failed || footerError) &&
        !validation?.fields?.includes('name') &&
        !validation?.fields?.includes('event_name') && (
          <StyledFormError>{validation?.message || DEFAULT_REQUIRED_ERROR_MESSAGE}</StyledFormError>
        )}
      <SubTitle>Mapping Rules</SubTitle>
      <MappingRulesBuilder
        validation={validation}
        rules={criticalEvent?.rules}
        onChange={handleRulesChange}
      />
      <ActionsWrapper>
        <Button onClick={handleCancelClick}>{cancelText ?? 'Cancel'}</Button>
        <Popconfirm
          open={popconfirmVisible}
          title="Confirm Saving"
          description={
            queryResult ? (
              <div style={{ maxWidth: '400px' }}>
                <p style={{ marginBottom: '8px' }}>
                  Event seen{' '}
                  <strong>
                    {(() => {
                      // Try to find count/occurrences/times
                      const countKey = 'count_of_records';
                      return queryResult.data[countKey] ?? 'NA';
                    })()}
                  </strong>{' '}
                  times on{' '}
                  <strong>
                    {queryResult.data?.device_conversion_rate != null
                      ? formatPercentage(Number(queryResult.data?.device_conversion_rate))
                      : 'NA'}
                  </strong>{' '}
                  of devices in last hour.
                </p>
                <p style={{ marginBottom: 0 }}>
                  Confirm it’s a <strong>specific user action</strong>—broad events may distort
                  journey insights.
                </p>
              </div>
            ) : (
              'Loading...'
            )
          }
          okText="Confirm & Save"
          cancelText="Back to Edit"
          okButtonProps={{ type: 'primary' }}
          cancelButtonProps={{ type: 'default' }}
          onConfirm={handleConfirmAndSave}
          onCancel={handleBackToEdit}
          onOpenChange={(visible) => {
            if (!visible) {
              handleBackToEdit();
            }
          }}
        >
          <Button
            style={!isEmptyRules ? { color: '#0046ff', border: '1px solid #0046ff' } : undefined}
            disabled={isEmptyRules}
            onClick={handleSaveClick}
            loading={isLoading || isQuerying}
          >
            Save
          </Button>
        </Popconfirm>
      </ActionsWrapper>
    </Wrapper>
  );
};

export default React.memo(CriticalEventForm);
